import { motion } from 'framer-motion';
import { Fingerprint, Landmark, Timer } from 'lucide-react';

const chapters = [
  { number: '01', icon: Timer, title: 'Speed of absorption', text: 'We engineer demand before launch, compressing sales cycles without discounting the asset.' },
  { number: '02', icon: Fingerprint, title: 'Title-first diligence', text: 'Every plotted mandate is filtered for clear ownership, usable approach, and credible appreciation drivers.' },
  { number: '03', icon: Landmark, title: 'Developer exclusivity', text: 'We work as a mandate partner, not a listing marketplace — preserving price integrity and buyer trust.' },
];

const About = () => {
  return (
    <section id="about" className="relative overflow-hidden bg-[#F5F5F5] py-24 sm:py-32" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-[0.72fr_1.28fr]">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="lg:sticky lg:top-32 lg:self-start">
            <div className="mb-5 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Manifesto</div>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-[#0B1F3B] sm:text-4xl">
              Real estate is not sold. It is positioned, proven, and absorbed.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#0B1F3B]/70">
              Next Leap Inc operates between serious developers and decisive investors, bringing discipline to one of India’s most emotional purchase categories.
            </p>
          </motion.div>

          <div className="grid gap-5">
            {chapters.map((chapter, index) => {
              const Icon = chapter.icon;
              return (
                <motion.article key={chapter.number} initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.65, delay: index * 0.1 }} className="group grid gap-6 rounded-[2rem] border border-[#0B1F3B]/10 bg-white p-6 shadow-[0_18px_60px_rgba(6,16,30,0.06)] transition-all duration-500 hover:-translate-y-1 hover:border-[#C5A880]/55 hover:shadow-[0_28px_80px_rgba(6,16,30,0.11)] sm:grid-cols-[88px_1fr] sm:p-8" data-testid={`manifesto-chapter-${chapter.number}`}>
                  <div>
                    <div className="font-tech text-sm text-[#C5A880]">{chapter.number}</div>
                    <div className="mt-5 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#0B1F3B] text-white transition-transform duration-500 group-hover:rotate-6 group-hover:scale-105">
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-semibold tracking-tight text-[#0B1F3B] sm:text-3xl">{chapter.title}</h3>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#0B1F3B]/65">{chapter.text}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.75 }} className="mt-20 overflow-hidden rounded-[2rem] bg-[#0B1F3B] text-[#F5F5F5] shadow-[0_35px_100px_rgba(6,16,30,0.25)]" data-testid="directors-section">
          <div className="blueprint-grid-dark grid gap-10 p-8 sm:p-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="mb-4 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Leadership</div>
              <h3 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">Two directors. One mandate standard.</h3>
              <p className="mt-5 text-sm leading-relaxed text-white/60">Direct, accountable leadership across plotted development sales, residential positioning, developer negotiation, and investor servicing.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ['VP', 'Vikrant Pawar', 'Plotted growth strategy / Client relations'],
                ['BK', 'Bhagwandas Kewat', 'Residential mandates / Developer partnerships'],
              ].map(([initials, name, role]) => (
                <div key={name} className="rounded-3xl border border-white/10 bg-white/[0.045] p-6 backdrop-blur-sm">
                  <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A880]/45 bg-[#C5A880]/10 font-display text-2xl text-[#C5A880]">{initials}</div>
                  <h4 className="font-display text-2xl font-semibold">{name}</h4>
                  <p className="mt-2 font-tech text-[11px] uppercase leading-relaxed tracking-[0.16em] text-white/50">{role}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
